/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import { useEffect, useState } from "react";
import type { Schedule, Server, SpeedTestResult, TestType } from "../types/types.ts";

/** Selects the geographic source used to add servers to the server catalogue. */
export type SpeedtestServerSource = "local" | "global" | "coordinates";

/** Browser-persisted Speedtest.net server discovery and display preferences. */
export type SpeedtestSettings =
  | { source: "local"; showServerCity: boolean }
  | { source: "global"; showServerCity: boolean }
  | {
      source: "coordinates";
      latitude: number;
      longitude: number;
      showServerCity: boolean;
    };

/** Editable settings state, which may contain incomplete coordinates before validation. */
export interface SpeedtestSettingsDraft {
  source: SpeedtestServerSource;
  latitude?: number;
  longitude?: number;
  showServerCity: boolean;
}

/** Query parameters accepted by the Speedtest.net server-list API. */
export interface SpeedtestServerQuery {
  global?: boolean;
  latitude?: number;
  longitude?: number;
  /** Fetches the source again even when it is already stored. */
  refresh?: boolean;
}

/** Identifies all origin-specific views of the retained Speedtest.net catalogue. */
export const speedtestServerCatalogueQueryKey = () => ["servers", "speedtest", "catalogue"] as const;

/** Identifies the retained Speedtest.net catalogue as ordered from one discovery origin. */
export const speedtestServerQueryKey = (query: SpeedtestServerQuery) =>
  [...speedtestServerCatalogueQueryKey(), query] as const;

/** Identifies durable fetch status for one Speedtest.net discovery source. */
export const speedtestServerStatusQueryKey = (settings: SpeedtestSettings) =>
  settings.source === "coordinates"
    ? ["servers", "speedtest", "status", settings.source, settings.latitude, settings.longitude] as const
    : ["servers", "speedtest", "status", settings.source] as const;

/** Formats the selected source's stored state. */
export const formatSpeedtestServerStorageStatus = (
  sourceLabel: string,
  status: {
    stored?: boolean;
    failedRegions?: number;
    totalRegions?: number;
    isLoading: boolean;
    isError: boolean;
  },
): string => {
  if (status.isLoading) return "Checking discovery status…";
  if (status.isError) return "Discovery status unavailable";
  if (status.stored && status.failedRegions && status.totalRegions) {
    return `Last fetch incomplete: ${status.totalRegions - status.failedRegions} of ${status.totalRegions} regions`;
  }
  return status.stored
    ? `${sourceLabel} discovery completed`
    : `No completed ${sourceLabel.toLowerCase()} discovery yet`;
};

const SETTINGS_KEY = "netronome-speedtest-settings";
const SETTINGS_EVENT = "speedtestSettingsChanged";
const DEFAULT_SETTINGS: SpeedtestSettings = {
  source: "local",
  showServerCity: false,
};

/** Reports whether a value is a finite latitude accepted by server discovery. */
export const isLatitude = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value) && value >= -90 && value <= 90;

/** Reports whether a value is a finite longitude accepted by server discovery. */
export const isLongitude = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value) && value >= -180 && value <= 180;

/** Normalizes untrusted persisted settings, falling back to local discovery when coordinates are invalid. */
export const normalizeSpeedtestSettings = (value: unknown): SpeedtestSettings => {
  if (typeof value !== "object" || value === null) {
    return { ...DEFAULT_SETTINGS };
  }

  const saved = value as Record<string, unknown>;
  const showServerCity = saved.showServerCity === true;
  if (saved.source === "global") {
    return { source: "global", showServerCity };
  }
  if (
    saved.source === "coordinates" &&
    isLatitude(saved.latitude) &&
    isLongitude(saved.longitude)
  ) {
    return {
      source: "coordinates",
      latitude: saved.latitude,
      longitude: saved.longitude,
      showServerCity,
    };
  }
  return { source: "local", showServerCity };
};

/** Reads Speedtest.net preferences from local storage. */
export const getSpeedtestSettings = (): SpeedtestSettings => {
  if (typeof window === "undefined") {
    return { ...DEFAULT_SETTINGS };
  }
  try {
    const saved = window.localStorage.getItem(SETTINGS_KEY);
    return saved ? normalizeSpeedtestSettings(JSON.parse(saved)) : { ...DEFAULT_SETTINGS };
  } catch (error) {
    console.warn("Failed to load speedtest settings from localStorage:", error);
    return { ...DEFAULT_SETTINGS };
  }
};

/** Persists Speedtest.net preferences, notifies active views, and reports whether storage succeeded. */
export const saveSpeedtestSettings = (settings: SpeedtestSettingsDraft): boolean => {
  try {
    const normalized = normalizeSpeedtestSettings(settings);
    window.localStorage.setItem(SETTINGS_KEY, JSON.stringify(normalized));
    window.dispatchEvent(new CustomEvent<SpeedtestSettings>(SETTINGS_EVENT, { detail: normalized }));
    return true;
  } catch (error) {
    console.warn("Failed to save speedtest settings to localStorage:", error);
    return false;
  }
};

/** Subscribes a component to browser-persisted Speedtest.net preferences. */
export const useSpeedtestSettings = (): SpeedtestSettings => {
  const [settings, setSettings] = useState<SpeedtestSettings>(getSpeedtestSettings);

  useEffect(() => {
    const handleChange = (event: Event) => {
      setSettings((event as CustomEvent<SpeedtestSettings>).detail);
    };
    window.addEventListener(SETTINGS_EVENT, handleChange);
    return () => window.removeEventListener(SETTINGS_EVENT, handleChange);
  }, []);

  return settings;
};

/** Converts persisted discovery preferences into server-list request parameters. */
export const speedtestServerQuery = (settings: SpeedtestSettings): SpeedtestServerQuery => {
  if (settings.source === "global") {
    return { global: true };
  }
  if (settings.source === "coordinates") {
    return { latitude: settings.latitude, longitude: settings.longitude };
  }
  return {};
};

/** Formats a result's provider name with its recorded city when enabled and available. */
export const formatSpeedtestServerName = (
  serverName: string,
  serverCity: string | null | undefined,
  showServerCity: boolean,
): string => {
  const city = serverCity?.trim();
  return showServerCity && city ? `${serverName} (${city})` : serverName;
};

/** Returns the provider-qualified identity used to filter and group historical server results. */
export const speedtestResultServerKey = (
  result: Pick<SpeedTestResult, "testType" | "serverId" | "serverHost" | "serverName">,
): string => `${result.testType}:${result.serverId || result.serverHost || result.serverName}`;

/** Finds a saved schedule's server without crossing provider or LibreSpeed source boundaries. */
export const findScheduleServer = (
  servers: Server[],
  serverID: string,
  options: Schedule["options"],
): Server | undefined => {
  const expectsLibrespeed = options.useLibrespeed === true;
  return servers.find((server) =>
    server.id === serverID &&
    Boolean(server.isLibrespeed) === expectsLibrespeed &&
    (!expectsLibrespeed || Boolean(server.isPublic) === (options.isPublicServer === true))
  );
};

/** The servers that the user chose, and the test type they chose them for. */
export interface ServerSelection {
  testType: TestType;
  servers: Server[];
}

/**
 * Returns the chosen servers that a test of testType can use.
 * A selection for a different test type gives no servers.
 * A chosen Speedtest.net server that left the list, for example after a source change, is not selected.
 * A server added by ID stays selected.
 */
export const selectedServersFor = (
  selection: ServerSelection,
  testType: TestType,
  servers: Server[]
): Server[] => {
  if (selection.testType !== testType) return [];
  if (testType !== "speedtest" || servers.length === 0) return selection.servers;
  return selection.servers.filter(
    (selected) => selected.isCustom || servers.some((server) => server.id === selected.id)
  );
};
